<template>
  <div class="pds-sidebar-nav">
    <!-- Section: Centers -->
    <div v-if="!userManagerStore.isExternalSession" class="pds-section">
      <p class="pds-section-title">{{ $t('profile.centerSettings') }}</p>
      <router-link
        v-for="link in centerLinks"
        :key="link.routeName + '-' + link.id"
        :to="{ name: link.routeName, params: link.params }"
        class="pds-tab"
        :class="{ 'pds-tab--active': isActive(link.routeName, link.params) }"
      >
        <component :is="link.icon" class="pds-tab-icon" />
        <span class="pds-tab-label">{{ link.label }}</span>
      </router-link>
      <router-link :to="{ name: 'CenterSignin', query: { context: 'add' } }" class="pds-tab pds-tab--add">
        <PlusCircleIcon class="pds-tab-icon" style="color: #FF9E1C;" />
        <span class="pds-tab-label" style="color: #FF9E1C;">{{ $t('profile.links.add_center') }}</span>
      </router-link>
    </div>

    <!-- Section: Account -->
    <div class="pds-section">
      <p class="pds-section-title">{{ $t('profile.accountSettings') }}</p>
      <router-link
        v-for="link in accountLinks"
        :key="link.routeName"
        :to="{ name: link.routeName }"
        class="pds-tab"
        :class="{ 'pds-tab--active': isActive(link.routeName) }"
      >
        <component :is="link.icon" class="pds-tab-icon" />
        <span class="pds-tab-label">{{ link.label }}</span>
      </router-link>
    </div>
  </div>

  <!-- Bottom: back to dashboard -->
  <router-link :to="{ name: 'Dashboard' }" class="pds-back-link">
    <ArrowLeftIcon class="pds-back-icon" />
    <span class="pds-back-label">{{ $t('common.dashboard_title') }}</span>
  </router-link>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useUserManagerStore } from '../../stores/userManagerStore'
import { isQrLoginDisabledOnWeb } from '../../utils/featureFlags'
import {
  AdjustmentsVerticalIcon,
  ArrowLeftIcon,
  DevicePhoneMobileIcon,
  ShieldCheckIcon,
  InformationCircleIcon,
  PlusCircleIcon,
  ComputerDesktopIcon,
} from '@heroicons/vue/24/outline'

const { t } = useI18n()
const route = useRoute()
const userManagerStore = useUserManagerStore()

const centerLinks = computed(() => {
  const centers = userManagerStore.user?.centers ?? []
  return centers.map(center => ({
    label: center.preferences?.label || center.title,
    routeName: 'ProfileCenter',
    params: { id: center.id },
    icon: AdjustmentsVerticalIcon,
    id: center.id,
  }))
})

const accountLinks = computed(() => {
  const links = [
    { label: t('profile.links.display'), routeName: 'DisplaySettings', icon: DevicePhoneMobileIcon },
  ]
  if (!userManagerStore.isExternalSession) {
    links.push({ label: t('profile.links.security'), routeName: 'Security', icon: ShieldCheckIcon })
  }
  if (!isQrLoginDisabledOnWeb()) {
    links.push({ label: t('qrlogin.profile_link'), routeName: 'ProfileQrLogin', icon: ComputerDesktopIcon })
  }
  links.push({ label: t('profile.links.about'), routeName: 'About', icon: InformationCircleIcon })
  return links
})

function isActive(routeName: string, params?: Record<string, unknown>) {
  if (route.name !== routeName) return false
  if (params) {
    return Object.entries(params).every(
      ([key, value]) => String(route.params[key]) === String(value)
    )
  }
  return true
}
</script>

<style scoped>
.pds-sidebar-nav {
  flex: 1;
  overflow-y: auto;
  padding: 8px 10px 8px 0;
}

.pds-section {
  margin-bottom: 8px;
}

.pds-section-title {
  font-family: 'Lato', sans-serif;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--muted-foreground);
  padding: 10px 14px 6px 16px;
  opacity: 0.7;
}

.pds-tab {
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 9px 14px 9px 16px;
  border-radius: 0 10px 10px 0;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  -webkit-tap-highlight-color: transparent;
  user-select: none;
  position: relative;
}

.pds-tab-icon {
  width: 17px;
  height: 17px;
  stroke-width: 1.9;
  color: var(--muted-foreground);
  transition: color 0.2s ease;
  flex-shrink: 0;
  opacity: 0.7;
}

.pds-tab-label {
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: var(--muted-foreground);
  text-align: left;
  line-height: 1;
  transition: color 0.2s ease, font-weight 0.2s ease;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  letter-spacing: 0.01em;
}

/* Hover */
.pds-tab:hover:not(.pds-tab--active):not(.pds-tab--add) {
  background: rgba(0, 0, 0, 0.025);
}
.pds-tab:hover:not(.pds-tab--active):not(.pds-tab--add) .pds-tab-icon {
  opacity: 1;
  color: var(--foreground);
}
.pds-tab:hover:not(.pds-tab--active):not(.pds-tab--add) .pds-tab-label {
  color: var(--foreground);
}
:is(.dark *).pds-tab:hover:not(.pds-tab--active):not(.pds-tab--add) {
  background: rgba(255, 255, 255, 0.035);
}

/* Active state */
.pds-tab--active {
  background: rgba(6, 200, 255, 0.05);
}
.pds-tab--active .pds-tab-icon {
  color: #06C8FF;
  opacity: 1;
}
.pds-tab--active .pds-tab-label {
  color: var(--foreground);
  font-weight: 700;
}
:is(.dark *).pds-tab--active {
  background: rgba(6, 200, 255, 0.07);
}

/* Active indicator — left edge bar */
.pds-tab--active::before {
  content: '';
  position: absolute;
  left: 0;
  top: 8px;
  bottom: 8px;
  width: 2.5px;
  border-radius: 0 2px 2px 0;
  background: #06C8FF;
  box-shadow: 0 0 8px rgba(6, 200, 255, 0.4);
}
:is(.dark *).pds-tab--active::before {
  box-shadow: 0 0 12px rgba(6, 200, 255, 0.3);
}

/* Add center link */
.pds-tab--add:hover {
  background: rgba(255, 158, 28, 0.05);
}

/* Back to dashboard link */
.pds-back-link {
  margin-top: auto;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 16px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  text-decoration: none;
  transition: background 0.15s ease;
}
.pds-back-link:hover {
  background: rgba(0, 0, 0, 0.025);
}
:is(.dark *).pds-back-link {
  border-top-color: rgba(255, 255, 255, 0.06);
}
:is(.dark *).pds-back-link:hover {
  background: rgba(255, 255, 255, 0.035);
}
.pds-back-icon {
  width: 15px;
  height: 15px;
  color: var(--muted-foreground);
  stroke-width: 2;
  flex-shrink: 0;
}
.pds-back-label {
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: var(--muted-foreground);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
